import { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEditorStore } from '../../stores/editor-store';


interface StylesPanelProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function StylesPanel({ collapsed = false, onToggle }: StylesPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editor = useEditorStore((s) => s.editorInstance);

  useEffect(() => {
    if (!editor || !containerRef.current || collapsed) return;

    const styleManager = editor.StyleManager;
    const container = containerRef.current;

    // Clear container first
    container.innerHTML = '';

    // Render styles into our container
    const stylesEl = styleManager.render();
    if (stylesEl) {
      container.appendChild(stylesEl);
    }

    // Update styles when component is selected
    const updateStyles = () => {
      const selected = editor.getSelected();
      if (selected) {
        // Refresh style manager for selected component
        styleManager.render();
      }
    };

    editor.on('component:selected', updateStyles);

    return () => {
      editor.off('component:selected', updateStyles);
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [editor, collapsed]);

  return (
    <div className="flex flex-col h-full bg-surface border-l border-surface-lighter">
      <div
        className={`py-3 border-b border-surface-lighter flex gap-2 ${collapsed ? 'px-2 items-center justify-center' : 'px-3 items-start justify-between'
          }`}
      >
        {!collapsed && (
          <div>
            <h3 className="text-sm font-medium text-text">Styles</h3>
            <p className="text-xs text-text-muted mt-0.5">Select an element to edit</p>
          </div>
        )}
        {onToggle && (
          <button
            onClick={onToggle}
            className="text-text-muted hover:text-text transition-colors"
            title={collapsed ? 'Expand styles panel' : 'Collapse styles panel'}
          >
            {collapsed ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        )}
      </div>
      {!collapsed && (
        <div
          ref={containerRef}
          className="flex-1 overflow-auto styles-container"
        />
      )}
    </div>
  );
}
